import React, { useEffect, useState } from "react";
import ReactGA from "react-ga";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import Alert from "../components/Alert";

ReactGA.pageview(window.location.pathname + window.location.search);

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [alert, setAlert] = useState({ status: "", message: "" });
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      const cart = JSON.parse(savedCart);
      setCartItems(cart);
      calculateTotal(cart);
    }
  }, []);

  const calculateTotal = (items) => {
    const totalAmount = items.reduce((acc, item) => {
      return acc + item.price * item.quantity;
    }, 0);
    setTotal(totalAmount.toFixed(2)); // round to 2 decimal places
  };

  const updateQuantity = (index, amount) => {
    const updatedCart = [...cartItems];
    const item = updatedCart[index];

    // Check if the edition is "Original Artwork"
    if (item.edition === "Original Art Work" && amount > 0) {
      // Trigger alert if user tries to increment quantity
      setAlert({
        status: "error",
        message:
          "You cannot add more than one for an Original Artwork edition.",
      });
      return;
    }

    item.quantity += amount;
    if (item.quantity < 1) item.quantity = 1;

    setCartItems(updatedCart);
    calculateTotal(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  const clearCart = () => {
    setCartItems([]);
    setTotal(0);
    localStorage.removeItem("cart");
  };

  const removeItem = (index) => {
    const updatedCart = cartItems.filter((_, i) => i !== index);
    setCartItems(updatedCart);
    calculateTotal(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };


  
  // Helper functions for invoice creation
  const createInvoice = async (item) => {
    try {
      const response = await fetch('http://localhost:3001/api/v1/payments/create-invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          item,
          customerInfo: {
            email: "johndoe@test.com", // Replace with actual user data
            phoneNumber: "0781234567",
            name: "John Doe",
          }
        }),
      });
  
      const data = await response.json();
      if (data.status === 'success') {
        return data.data.invoiceNumber;
      } else {
        throw new Error(data.message || 'Invoice creation failed');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
  };
  
  const createBatchInvoice = async (invoiceNumbers) => {
    try {
      const response = await fetch('/api/v1/payments/create-batch-invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoiceNumbers }),
      });
  
      const data = await response.json();
      if (data.status === 'success') {
        return data.data.invoiceNumber;
      } else {
        throw new Error(data.message || 'Batch invoice creation failed');
      }
    } catch (error) {
      console.error('Error creating batch invoice:', error);
      throw error;
    }
  };

  // Checkout handler: creates invoices then initiates payment
  const handleCheckout = async () => {
    try {
      let invoiceNumber;
      if (cartItems.length === 1) {
        // Create a single invoice
        invoiceNumber = await createInvoice(cartItems[0]);
      } else if (cartItems.length > 1) {
        // Create individual invoices for each cart item
        const invoiceNumbers = [];
        for (const item of cartItems) {
          const invNumber = await createInvoice(item);
          invoiceNumbers.push(invNumber);
        }
        // Create a batch invoice from the individual invoice numbers
        invoiceNumber = await createBatchInvoice(invoiceNumbers);
      }

      // Once invoiceNumber is obtained, initiate payment
      if (window.IremboPay) {
        window.IremboPay.initiate({
          publicKey: "pk_live_a28096c404d746d78f32f302db16aa1f", // Replace with your public key
          invoiceNumber,
          locale: window.IremboPay.locale.EN,
          callback: (err, resp) => {
            if (!err) {
              console.log("Payment Success:", resp);
              clearCart(); // Optionally clear cart on success
            } else {
              console.error("Payment Error:", err);
            }
          },
        });
      } else {
        console.error("IremboPay script not loaded.");
      }
    } catch (error) {
      console.error("Error during checkout:", error);
    }
  };

  return (
    <div>
      <NavBar />
      {alert.message && (
        <Alert
          status={alert.status}
          message={alert.message}
          onClose={() => setAlert({ status: "", message: "" })}
        />
      )}
      <div className="bg-gray-100 min-h-screen py-8">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl font-semibold mb-4">Shopping Cart</h1>
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Cart Items Section */}
            <div className="lg:w-3/4">
              <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4">
                {cartItems.length > 0 ? (
                  cartItems.map((item, index) => (
                    <div
                      key={`${item.id}-${item.edition}-${index}`}
                      className="flex flex-col sm:flex-row items-center border-t py-4"
                    >
                      {/* Product Image */}
                      <div className="w-full sm:w-1/4">
                        <img
                          className="w-full h-24 object-cover rounded-md sm:h-32"
                          src={`${item.artImageCover}`}
                          alt={item.name}
                        />
                      </div>

                      {/* Product Info */}
                      <div className="w-full sm:w-2/4 sm:pl-4">
                        <span className="font-semibold">
                          {item.name} ({item.edition})
                        </span>
                        <div className="text-gray-500">${item.price}</div>
                      </div>

                      {/* Quantity Controls */}
                      <div className="w-full sm:w-1/4 flex justify-center items-center">
                        <button
                          className="border rounded-md py-1 px-3 mr-2"
                          onClick={() => updateQuantity(index, -1)}
                        >
                          -
                        </button>
                        <span className="text-center w-8">{item.quantity}</span>
                        <button
                          className="border rounded-md py-1 px-3 ml-2"
                          onClick={() => updateQuantity(index, 1)}
                        >
                          +
                        </button>
                      </div>

                      {/* Remove Item Button */}
                      <div className="w-full sm:w-1/4 flex justify-center sm:justify-end mt-4 sm:mt-0">
                        <button
                          className="text-red-500 border rounded-md py-2 px-3"
                          onClick={() => removeItem(index)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">Your cart is empty</div>
                )}

                {/* Clear Cart Button */}
                {cartItems.length > 0 && (
                  <button
                    className="bg-secondary text-white py-2 px-4 rounded-lg mt-4 w-full"
                    onClick={clearCart}
                  >
                    Clear Cart
                  </button>
                )}
              </div>
            </div>

            {/* Cart Summary Section */}
            <div className="lg:w-1/4">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-lg font-semibold mb-4">Summary</h2>
                <div className="flex justify-between mb-2">
                  <span>Subtotal</span>
                  <span>${total}</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">Total</span>
                  <span className="font-semibold">${total}</span>
                </div>
                <button className="bg-primary text-white py-2 px-4 rounded-lg mt-4 w-full">
                  Checkout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Cart;
